import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';
import { hasAnalyticsConsent } from './analytics';
import Cookies from 'js-cookie';
import { logger } from '../utils/logger';

import { debounce } from 'lodash';
export interface PhoneClickEvent {
  id: string;
  session_id: string;
  user_id?: string;
  timestamp: string;
  timezone: string;
  page_url: string;
  page_title: string;
  button_text: string;
  button_position: {
    x: number;
    y: number;
    section: string;
    element_id?: string;
    element_class?: string;
  };
  user_agent: string;
  device_info: {
    type: 'mobile' | 'tablet' | 'desktop';
    os: string;
    browser: string;
    screen_resolution: string;
    viewport_size: string;
  };
  user_journey: {
    pages_visited: string[];
    time_on_site: number;
    referrer: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
  };
  geographic_data?: {
    country?: string;
    region?: string;
    city?: string;
    timezone_offset: number;
  };
}

export interface PhoneTrackingStats {
  total_clicks: number;
  unique_sessions: number;
  hourly_breakdown: { hour: number; clicks: number }[];
  daily_breakdown: { date: string; clicks: number }[];
  weekly_breakdown: { week: string; clicks: number }[];
  monthly_breakdown: { month: string; clicks: number }[];
  top_pages: { page: string; clicks: number }[];
  device_breakdown: { device: string; clicks: number }[];
  geographic_breakdown: { location: string; clicks: number }[];
}

class PhoneTracker {
  private static instance: PhoneTracker;
  private sessionId: string;
  private userJourney: string[] = [];
  private sessionStartTime: number;
  private phoneNumber = '(615) 506-3451';
  private phoneNumberClean = '6155063451';

  public static getInstance(): PhoneTracker {
    if (!PhoneTracker.instance) {
      PhoneTracker.instance = new PhoneTracker();
    }
    return PhoneTracker.instance;
  }

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.sessionStartTime = Date.now();
    this.initializeTracking();
    this.trackPageVisit();
  }

  private getOrCreateSessionId(): string {
    let sessionId = Cookies.get('phone_tracking_session');
    if (!sessionId) {
      sessionId = uuidv4();
      Cookies.set('phone_tracking_session', sessionId, { 
        expires: 1, // 1 day
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:'
      });
    }
    return sessionId;
  }

  private trackPageVisit(): void {
    this.userJourney.push(window.location.pathname);
    
    // Keep only last 10 pages to prevent excessive data
    if (this.userJourney.length > 10) {
      this.userJourney = this.userJourney.slice(-10);
    }
  }

  private getDeviceInfo() {
    const userAgent = navigator.userAgent;
    
    // Detect device type
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isTablet = /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)/i.test(userAgent);
    
    let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
    if (isMobile && !isTablet) deviceType = 'mobile';
    else if (isTablet) deviceType = 'tablet';

    // Detect OS
    let os = 'Unknown';
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';

    // Detect browser
    let browser = 'Unknown';
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
    else if (userAgent.includes('Edg')) browser = 'Edge';

    return {
      type: deviceType,
      os,
      browser,
      screen_resolution: `${screen.width}x${screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`
    };
  }

  private async getGeographicData() {
    try {
      // Use a free IP geolocation service
      const response = await fetch('https://ipapi.co/json/', {
        signal: AbortSignal.timeout(3000) // 3 second timeout
      });
      
      if (response.ok) {
        const data = await response.json();
        return {
          country: data.country_name,
          region: data.region,
          city: data.city,
          timezone_offset: new Date().getTimezoneOffset()
        };
      }
    } catch (error) {
      logger.warn('Failed to get geographic data:', error);
    }
    
    return {
      timezone_offset: new Date().getTimezoneOffset()
    };
  }

  private getButtonSection(element: HTMLElement): string {
    // Determine which section of the page the button is in
    const sections = [
      { selector: 'header, nav', name: 'Header/Navigation' },
      { selector: '.hero, [class*="hero"]', name: 'Hero Section' },
      { selector: 'footer', name: 'Footer' },
      { selector: '[class*="contact"]', name: 'Contact Section' },
      { selector: 'aside, [role="complementary"]', name: 'Sidebar' },
      { selector: 'main', name: 'Main Content' }
    ];

    for (const section of sections) {
      const sectionElement = element.closest(section.selector);
      if (sectionElement) {
        return section.name;
      }
    }

    return 'Unknown Section';
  }

  private getUTMParameters(): { utm_source?: string; utm_medium?: string; utm_campaign?: string } {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utm_source: urlParams.get('utm_source') || undefined,
      utm_medium: urlParams.get('utm_medium') || undefined,
      utm_campaign: urlParams.get('utm_campaign') || undefined
    };
  }

  private async trackPhoneClick(element: HTMLElement): Promise<void> {
    if (!hasAnalyticsConsent()) {
      logger.info('Phone tracking skipped - no analytics consent');
      return;
    }

    try {
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      const clickEvent: PhoneClickEvent = {
        id: uuidv4(),
        session_id: this.sessionId,
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        page_url: window.location.href,
        page_title: document.title,
        button_text: element.textContent?.trim() || element.getAttribute('aria-label') || 'Phone Button',
        button_position: {
          x: rect.left + scrollLeft,
          y: rect.top + scrollTop,
          section: this.getButtonSection(element),
          element_id: element.id || undefined,
          element_class: element.className || undefined
        },
        user_agent: navigator.userAgent,
        device_info: this.getDeviceInfo(),
        user_journey: {
          pages_visited: [...this.userJourney],
          time_on_site: Date.now() - this.sessionStartTime,
          referrer: document.referrer,
          ...this.getUTMParameters()
        },
        geographic_data: await this.getGeographicData()
      };

      // Store in Supabase
      const { error } = await supabase
        .from('phone_clicks')
        .insert([clickEvent]);

      if (error) {
        logger.error('Failed to track phone click:', error);
        return;
      }

      // Track with Google Analytics
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'phone_click', {
          event_category: 'Phone Calls',
          event_label: clickEvent.button_position.section,
          custom_map: {
            dimension1: clickEvent.device_info.type,
            dimension2: clickEvent.button_position.section,
            dimension3: clickEvent.session_id
          }
        });
      }

      logger.debug('Phone click tracked successfully');
    } catch (error) {
      logger.error('Error tracking phone click:', error);
    }
  }

  private isPhoneElement(element: HTMLElement): boolean {
    const text = element.textContent || '';
    const href = element.getAttribute('href') || '';
    const ariaLabel = element.getAttribute('aria-label') || '';
    
    return (
      text.includes(this.phoneNumber) ||
      text.includes(this.phoneNumberClean) ||
      href.includes(`tel:${this.phoneNumberClean}`) ||
      href.includes(`tel:+1${this.phoneNumberClean}`) ||
      ariaLabel.toLowerCase().includes('call') ||
      ariaLabel.toLowerCase().includes('phone')
    );
  }

  private trackedElements = new WeakSet<HTMLElement>();
  private debouncedTrackPhoneClick = debounce((element: HTMLElement) => {
    this.trackPhoneClick(element);
  }, 300);

  private initializeTracking(): void {
    // Single document-level click listener with debouncing to prevent duplicates
    const trackClick = (event: Event) => {
      const element = event.target as HTMLElement;
      const phoneElement = element.closest('a[href*="tel:"]') as HTMLElement;

      if (phoneElement && this.isPhoneElement(phoneElement)) {
        // Prevent duplicate tracking for the same element
        if (this.trackedElements.has(phoneElement)) {
          return;
        }

        this.trackedElements.add(phoneElement);
        this.debouncedTrackPhoneClick(phoneElement);

        // Remove from tracked set after 1 second to allow future clicks
        setTimeout(() => {
          this.trackedElements.delete(phoneElement);
        }, 1000);
      }
    };

    document.addEventListener('click', trackClick, true); // Use capture phase

    // Track page navigation for user journey
    window.addEventListener('popstate', () => {
      this.trackPageVisit();
    });
  }

  public async getPhoneTrackingStats(
    startDate: Date,
    endDate: Date
  ): Promise<PhoneTrackingStats> {
    try {
      const { data: clicks, error } = await supabase
        .from('phone_clicks')
        .select('*')
        .gte('timestamp', startDate.toISOString())
        .lte('timestamp', endDate.toISOString())
        .order('timestamp', { ascending: false });

      if (error) throw error;

      const stats: PhoneTrackingStats = {
        total_clicks: clicks?.length || 0,
        unique_sessions: new Set(clicks?.map(c => c.session_id)).size,
        hourly_breakdown: this.generateHourlyBreakdown(clicks || []),
        daily_breakdown: this.generateDailyBreakdown(clicks || []),
        weekly_breakdown: this.generateWeeklyBreakdown(clicks || []),
        monthly_breakdown: this.generateMonthlyBreakdown(clicks || []),
        top_pages: this.generateTopPages(clicks || []),
        device_breakdown: this.generateDeviceBreakdown(clicks || []),
        geographic_breakdown: this.generateGeographicBreakdown(clicks || [])
      };

      return stats;
    } catch (error) {
      logger.error('Error fetching phone tracking stats:', error);
      throw error;
    }
  }

  private generateHourlyBreakdown(clicks: any[]): { hour: number; clicks: number }[] {
    const hourlyData: { [key: number]: number } = {};
    
    for (let i = 0; i < 24; i++) {
      hourlyData[i] = 0;
    }

    clicks.forEach(click => {
      const hour = new Date(click.timestamp).getHours();
      hourlyData[hour]++;
    });

    return Object.entries(hourlyData).map(([hour, clicks]) => ({
      hour: parseInt(hour),
      clicks
    }));
  }

  private generateDailyBreakdown(clicks: any[]): { date: string; clicks: number }[] {
    const dailyData: { [key: string]: number } = {};

    clicks.forEach(click => {
      const date = new Date(click.timestamp).toISOString().split('T')[0];
      dailyData[date] = (dailyData[date] || 0) + 1;
    });

    return Object.entries(dailyData).map(([date, clicks]) => ({
      date,
      clicks
    }));
  }

  private generateWeeklyBreakdown(clicks: any[]): { week: string; clicks: number }[] {
    const weeklyData: { [key: string]: number } = {};

    clicks.forEach(click => {
      const date = new Date(click.timestamp);
      const weekStart = new Date(date.setDate(date.getDate() - date.getDay()));
      const weekKey = weekStart.toISOString().split('T')[0];
      weeklyData[weekKey] = (weeklyData[weekKey] || 0) + 1;
    });

    return Object.entries(weeklyData).map(([week, clicks]) => ({
      week,
      clicks
    }));
  }

  private generateMonthlyBreakdown(clicks: any[]): { month: string; clicks: number }[] {
    const monthlyData: { [key: string]: number } = {};

    clicks.forEach(click => {
      const date = new Date(click.timestamp);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
    });

    return Object.entries(monthlyData).map(([month, clicks]) => ({
      month,
      clicks
    }));
  }

  private generateTopPages(clicks: any[]): { page: string; clicks: number }[] {
    const pageData: { [key: string]: number } = {};

    clicks.forEach(click => {
      const page = new URL(click.page_url).pathname;
      pageData[page] = (pageData[page] || 0) + 1;
    });

    return Object.entries(pageData)
      .map(([page, clicks]) => ({ page, clicks }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 10);
  }

  private generateDeviceBreakdown(clicks: any[]): { device: string; clicks: number }[] {
    const deviceData: { [key: string]: number } = {};

    clicks.forEach(click => {
      const device = click.device_info?.type || 'unknown';
      deviceData[device] = (deviceData[device] || 0) + 1;
    });

    return Object.entries(deviceData).map(([device, clicks]) => ({
      device,
      clicks
    }));
  }

  private generateGeographicBreakdown(clicks: any[]): { location: string; clicks: number }[] {
    const geoData: { [key: string]: number } = {};

    clicks.forEach(click => {
      const location = click.geographic_data?.city && click.geographic_data?.region
        ? `${click.geographic_data.city}, ${click.geographic_data.region}`
        : click.geographic_data?.region || 'Unknown';
      geoData[location] = (geoData[location] || 0) + 1;
    });

    return Object.entries(geoData)
      .map(([location, clicks]) => ({ location, clicks }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 10);
  }

  public async generateAlerts(): Promise<void> {
    try {
      const today = new Date();
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

      const [todayStats, yesterdayStats, weekStats] = await Promise.all([
        this.getPhoneTrackingStats(today, today),
        this.getPhoneTrackingStats(yesterday, yesterday),
        this.getPhoneTrackingStats(lastWeek, today)
      ]);

      // Alert for significant increase in calls
      if (todayStats.total_clicks > yesterdayStats.total_clicks * 2) {
        this.sendAlert('High Call Volume', `Today's phone clicks (${todayStats.total_clicks}) are significantly higher than yesterday (${yesterdayStats.total_clicks})`);
      }

      // Alert for no calls in 24 hours
      if (todayStats.total_clicks === 0 && yesterdayStats.total_clicks === 0) {
        this.sendAlert('No Phone Clicks', 'No phone button clicks recorded in the last 24 hours');
      }

      // Alert for unusual geographic activity
      const topLocation = weekStats.geographic_breakdown[0];
      if (topLocation && topLocation.clicks > weekStats.total_clicks * 0.5) {
        this.sendAlert('Geographic Anomaly', `Unusual concentration of calls from ${topLocation.location}: ${topLocation.clicks} clicks`);
      }

    } catch (error) {
      logger.error('Error generating alerts:', error);
    }
  }

  private async sendAlert(title: string, message: string): Promise<void> {
    try {
      // Store alert in database
      await supabase
        .from('phone_tracking_alerts')
        .insert([{
          title,
          message,
          timestamp: new Date().toISOString(),
          severity: 'info'
        }]);

      // You could also send email notifications here
      logger.info(`ALERT: ${title} - ${message}`);
    } catch (error) {
      logger.error('Failed to send alert:', error);
    }
  }
}

// Export singleton instance
export const phoneTracker = PhoneTracker.getInstance();

// Initialize tracking when module loads
if (typeof window !== 'undefined') {
  phoneTracker;
}